/**
 * Groundworx Carousel - Interactivity API Frontend
 *
 * Bootstraps Embla Carousel via the WordPress Interactivity API.
 * Reads resolved carouselOptions from context (PHP resolves the
 * stored attribute to runtime config with slideSize, loop, etc.).
 */

import {
	store,
	getElement,
	getContext,
	withSyncEvent,
} from '@wordpress/interactivity';

import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';

import { breakpoints as BREAKPOINTS } from '@groundworx/foundation';

// ---- Constants ----

const BREAKPOINT_ORDER = Object.keys( BREAKPOINTS );
const VALID_ALIGN = [ 'start', 'center', 'end' ];

const OPTION_DEFAULTS = {
	loop: false,
	align: 'start',
	slideSize: '100%',
	slideSizeMode: 'percent',
	slideGap: '',
	slidesToScroll: 1,
	containScroll: 'trimSnaps',
	autoplay: false,
	arrows: false,
	pagination: false,
	progressBar: false,
	counter: false,
	destroy: false,
	perPage: 1,
};

// ---- Pure utilities ----

const parseBreakpointPx = ( value ) => {
	if ( ! value ) return 0;
	return parseInt( value.replace( 'px', '' ), 10 ) || 0;
};

/**
 * Get effective value for a setting at the given viewport width.
 * Mobile-first cascade: starts with default, applies root, then breakpoints.
 */
const getEffectiveValue = ( defaultValue, options, key, width ) => {
	let value = defaultValue;
	if ( ! options ) return value;
	if ( options[ key ] !== undefined ) value = options[ key ];
	const bps = options.breakpoints;
	if ( ! bps ) return value;
	for ( const bpKey of BREAKPOINT_ORDER ) {
		const bpValue = BREAKPOINTS[ bpKey ];
		if ( ! bpValue ) continue;
		const bpPx = parseBreakpointPx( bpValue );
		const config = bps[ bpKey ];
		if ( width >= bpPx && config && config[ key ] !== undefined && config[ key ] !== null ) {
			value = config[ key ];
		}
	}
	return value;
};

// ---- Overflow detection ----

const checkHasOverflow = ( embla ) => {
	if ( ! embla ) return true;
	const containerNode = embla.containerNode();
	const viewportNode = embla.rootNode();
	return containerNode.scrollWidth > viewportNode.clientWidth;
};

// ---- ID generator ----

let idCounter = 0;
const generateId = () => `gwx-carousel-${ ++idCounter }`;

// ---- Store ----

const { state, actions } = store( 'groundworx/carousel', {

	state: {
		get canScrollPrev() {
			return getContext().canScrollPrev;
		},
		get canScrollNext() {
			return getContext().canScrollNext;
		},

		// IDs (for data-wp-bind).
		get trackId() {
			const ctx = getContext();
			return ctx.instanceId ? `${ ctx.instanceId }-track` : '';
		},
		get slideId() {
			const ctx = getContext();
			return ctx.instanceId && ctx.slideIndex !== undefined
				? `${ ctx.instanceId }-slide-${ ctx.slideIndex }`
				: '';
		},

		// Destroyed mode (for data-wp-class on root).
		get isDestroyed() {
			return getContext().destroy === true;
		},
		get isLoop() {
			return !! getContext().loop;
		},

		// Reactive CSS custom properties.
		// Note: --gwx-slide-size and --gwx-slide-gap are handled by PHP <style>
		// block (no JS needed — avoids redraw on hydration).
		get columnsCss() {
			const ctx = getContext();
			const size = ctx.slideSize || '';
			if ( ! size.includes( '%' ) ) return null;
			const percent = parseFloat( size );
			if ( ! percent || percent <= 0 ) return null;
			return String( Math.round( 100 / percent ) );
		},

		// Feature flags (for data-wp-class on root).
		get hasArrows() {
			return !! getContext().arrows;
		},
		get hasPagination() {
			return !! getContext().pagination;
		},
		get hasCounter() {
			return !! getContext().counter;
		},
		get hasProgressBar() {
			return !! getContext().progressBar;
		},

		// Overflow detection (for data-wp-class on root).
		get isOverflow() {
			const ctx = getContext();
			if ( ctx.destroy ) return false;
			return ctx.hasOverflow !== false;
		},
		get isNotOverflow() {
			const ctx = getContext();
			if ( ctx.destroy ) return false;
			return ctx.hasOverflow === false;
		},

		// Dots (for data-wp-each).
		get dots() {
			const ctx = getContext();
			return Array.from( { length: ctx.totalSlides || 0 }, ( _, i ) => i );
		},
		get isDotActive() {
			const ctx = getContext();
			return ctx.item === ctx.currentIndex;
		},
		get dotLabel() {
			const ctx = getContext();
			const gotoLabel = ctx.labels?.gotoSlide || 'Go to slide';
			return `${ gotoLabel } ${ ctx.item + 1 }`;
		},
		get dotNumber() {
			return getContext().item + 1;
		},
		get dotSlideId() {
			const ctx = getContext();
			return ctx.instanceId ? `${ ctx.instanceId }-slide-${ ctx.item }` : '';
		},
		get dotTabindex() {
			const ctx = getContext();
			return ctx.item === ctx.currentIndex ? 0 : -1;
		},

		// Dot custom colors (from context, picked by active state).
		get dotColor() {
			const ctx = getContext();
			return ( ctx.item === ctx.currentIndex ? ctx.dotActiveColor : ctx.dotInactiveColor ) || null;
		},
		get dotBgColor() {
			const ctx = getContext();
			return ( ctx.item === ctx.currentIndex ? ctx.dotActiveBg : ctx.dotInactiveBg ) || null;
		},
		get dotBorderColor() {
			const ctx = getContext();
			return ( ctx.item === ctx.currentIndex ? ctx.dotActiveBorder : ctx.dotInactiveBorder ) || null;
		},

		// Counter (for data-wp-text).
		get counterCurrent() {
			return ( getContext().currentIndex || 0 ) + 1;
		},
		get counterTotal() {
			return getContext().totalSlides || 0;
		},

		// Progress bar (for data-wp-style--width).
		get progressWidth() {
			const ctx = getContext();
			const total = ctx.totalSlides || 0;
			if ( total <= 1 ) return '100%';
			return `${ ( ( ctx.currentIndex + 1 ) / total ) * 100 }%`;
		},
	},

	actions: {
		scrollPrev() {
			const { embla } = getContext();
			if ( embla ) embla.scrollPrev();
		},
		scrollNext() {
			const { embla } = getContext();
			if ( embla ) embla.scrollNext();
		},
		scrollToDot() {
			const ctx = getContext();
			if ( ctx.embla ) ctx.embla.scrollTo( ctx.item );
		},

		/**
		 * Keyboard navigation for pagination dots (tablist pattern).
		 */
		handleDotKeydown: withSyncEvent( ( event ) => {
			const { ref } = getElement();
			const ctx = getContext();

			const tablist = ref.closest( '[role="tablist"]' );
			if ( ! tablist ) return;

			const tabs = Array.from( tablist.querySelectorAll( '[role="tab"]' ) );
			if ( tabs.length === 0 ) return;

			const currentIndex = tabs.indexOf( ref );
			if ( currentIndex === -1 ) return;

			let newIndex = currentIndex;

			if ( event.key === 'ArrowLeft' || event.key === 'ArrowUp' ) {
				event.preventDefault();
				newIndex = ( currentIndex - 1 + tabs.length ) % tabs.length;
			} else if ( event.key === 'ArrowRight' || event.key === 'ArrowDown' ) {
				event.preventDefault();
				newIndex = ( currentIndex + 1 ) % tabs.length;
			} else if ( event.key === 'Home' ) {
				event.preventDefault();
				newIndex = 0;
			} else if ( event.key === 'End' ) {
				event.preventDefault();
				newIndex = tabs.length - 1;
			}

			if ( newIndex !== currentIndex ) {
				if ( ctx.embla ) ctx.embla.scrollTo( newIndex );
				tabs[ newIndex ].focus();
			}
		} ),

		/**
		 * Pause autoplay on hover.
		 */
		pauseAutoplay() {
			const ctx = getContext();
			if ( ! ctx.embla ) return;
			const plugins = ctx.embla.plugins();
			if ( ctx.autoplay && plugins?.autoplay ) plugins.autoplay.stop();
		},

		/**
		 * Resume autoplay on hover leave.
		 */
		resumeAutoplay() {
			const ctx = getContext();
			if ( ! ctx.embla ) return;
			const plugins = ctx.embla.plugins();
			if ( ctx.autoplay && plugins?.autoplay ) plugins.autoplay.play();
		},

		/**
		 * Resolve carouselOptions for the current viewport width
		 * and spread effective values into context.
		 */
		resolveOptions() {
			const ctx = getContext();
			const width = window.innerWidth;
			for ( const [ key, fallback ] of Object.entries( OPTION_DEFAULTS ) ) {
				ctx[ key ] = getEffectiveValue( fallback, ctx.carouselOptions || {}, key, width );
			}
		},

		/**
		 * Recalculate the current breakpoint from window width.
		 */
		updateBreakpoint() {
			const ctx = getContext();
			const width = window.innerWidth;
			let bp = 'default';
			for ( const bpKey of BREAKPOINT_ORDER ) {
				const bpValue = BREAKPOINTS[ bpKey ];
				if ( ! bpValue ) continue;
				const bpPx = parseBreakpointPx( bpValue );
				if ( bpPx && width >= bpPx ) {
					bp = bpKey;
				}
			}
			ctx.currentBreakpoint = bp;
		},

		/**
		 * Create Embla instance from current context values.
		 */
		createEmbla() {
			const ctx = getContext();

			if ( ! ctx.viewport ) return;
			// Build plugins.
			const plugins = [];
			if ( ctx.autoplay ) {
				plugins.push(
					Autoplay( {
						delay: 4000,
						stopOnInteraction: false,
						stopOnMouseEnter: false,
					} )
				);
			}

			const align = VALID_ALIGN.includes( ctx.align ) ? ctx.align : 'start';

			const embla = EmblaCarousel( ctx.viewport, {
				loop: ctx.loop ?? false,
				align,
				slidesToScroll: ctx.slidesToScroll ?? 1,
				containScroll: ctx.containScroll || false,
				duration: 20,
				skipSnaps: true,
				watchSlides: true,
				watchResize: true,
				watchDrag: ( emblaApi ) => checkHasOverflow( emblaApi ),
			}, plugins );

			ctx.embla = embla;

			// Sync context on Embla events.
			const syncState = () => {
				ctx.currentIndex = embla.selectedScrollSnap();
				ctx.canScrollPrev = embla.canScrollPrev();
				ctx.canScrollNext = embla.canScrollNext();
			};

			ctx.totalSlides = embla.scrollSnapList().length;

			embla.on( 'select', syncState );
			embla.on( 'reInit', () => {
				ctx.totalSlides = embla.scrollSnapList().length;
				syncState();
			} );

			// Overflow detection.
			const updateOverflow = () => {
				const hasOverflow = checkHasOverflow( embla );
				ctx.hasOverflow = hasOverflow;
			};
			updateOverflow();
			embla.on( 'reInit', updateOverflow );
			embla.on( 'resize', updateOverflow );

			// Slide visibility + inert management.
			const updateSlideVisibility = () => {
				const slides = embla.slideNodes();
				const inView = embla.slidesInView( true );
				slides.forEach( ( slide, index ) => {
					const isVisible = inView.includes( index );
					slide.setAttribute( 'aria-hidden', isVisible ? 'false' : 'true' );
					if ( isVisible ) {
						slide.removeAttribute( 'inert' );
					} else {
						slide.setAttribute( 'inert', 'true' );
					}
				} );
			};
			embla.on( 'select', updateSlideVisibility );
			embla.on( 'slidesInView', updateSlideVisibility );
			updateSlideVisibility();

			// Drag feedback.
			embla.on( 'pointerDown', () => {
				embla.containerNode().classList.add( 'is-dragging' );
			} );
			embla.on( 'pointerUp', () => {
				embla.containerNode().classList.remove( 'is-dragging' );
			} );

			// Accessibility: set IDs on slides.
			const slides = embla.slideNodes();
			slides.forEach( ( slide, i ) => {
				slide.id = `${ ctx.instanceId }-slide-${ i }`;
				slide.setAttribute( 'role', 'tabpanel' );
				slide.setAttribute( 'aria-roledescription', ctx.labels?.slide || 'slide' );
			} );

			syncState();
		},

		/**
		 * Destroy the current Embla instance.
		 */
		destroyEmbla() {
			const ctx = getContext();
			if ( ctx.embla ) {
				// Release all slides.
				const slides = ctx.embla.slideNodes();
				slides.forEach( ( slide ) => {
					slide.removeAttribute( 'inert' );
					slide.removeAttribute( 'aria-hidden' );
					slide.removeAttribute( 'role' );
					slide.removeAttribute( 'aria-roledescription' );
				} );
				ctx.embla.containerNode().classList.remove( 'is-dragging' );
				ctx.embla.destroy();
			}
			ctx.embla = null;
		},
	},

	callbacks: {
		/**
		 * Root init — runs AFTER children (bottom-up effect order).
		 * Viewport ref is already in context from initViewport.
		 */
		carouselInit() {
			const { ref } = getElement();
			const ctx = getContext();

			// Store root ref.
			ctx.rootEl = ref;

			// Resolve effective options for current breakpoint.
			actions.updateBreakpoint();
			actions.resolveOptions();

			// Initialize carousel state.
			ctx.currentIndex = 0;
			ctx.totalSlides = 0;
			ctx.canScrollPrev = false;
			ctx.canScrollNext = false;
			ctx.hasOverflow = true;

			// Instance ID for accessibility.
			ctx.instanceId = generateId();

			// Only create Embla for non-destroyed mode.
			if ( ctx.viewport && ! ctx.destroy ) {
				ctx.viewport.id = `${ ctx.instanceId }-track`;
				actions.createEmbla();

				// Settle reflow — reInit after paint to correct any
				// measurement drift from late CSS custom property application.
				ctx._rafId = requestAnimationFrame( () => {
					if ( ctx.embla ) {
						ctx.embla.reInit();
					}
				} );
			}

			return () => {
				if ( ctx._rafId ) cancelAnimationFrame( ctx._rafId );
				if ( ctx.embla ) ctx.embla.destroy();
				ctx.embla = null;
			};
		},

		/**
		 * Viewport init — stores ref in context.
		 * Runs BEFORE carouselInit (bottom-up effect order).
		 */
		initViewport() {
			const { ref } = getElement();
			getContext().viewport = ref;
		},

		/**
		 * Watcher — responds to breakpoint changes.
		 * Resolves new option values and reinitializes Embla.
		 * Destroy is one-way: once destroyed, stays destroyed for all larger breakpoints.
		 */
		carouselReinit() {
			const { ref } = getElement();
			const ctx = getContext();

			// Reading currentBreakpoint creates the reactive dependency.
			// eslint-disable-next-line no-unused-expressions
			ctx.currentBreakpoint;

			// Skip initial run — carouselInit handles first setup.
			if ( ! ref._breakpointInit ) {
				ref._breakpointInit = true;
				return;
			}

			const wasDestroyed = ctx.destroy;
			actions.resolveOptions();

			if ( ctx.destroy ) {
				// Entering or staying in destroyed/grid mode.
				if ( ! wasDestroyed ) {
					actions.destroyEmbla();
				}
			} else {
				// Carousel mode — destroy+create to handle option changes.
				const currentIndex = ctx.embla?.selectedScrollSnap() ?? 0;
				if ( ctx.embla ) {
					actions.destroyEmbla();
				}
				actions.createEmbla();
				// Restore slide position after rebuild.
				if ( ctx.embla && currentIndex > 0 ) {
					ctx.embla.scrollTo( currentIndex, true );
				}
			}
		},
	},

} );
